import { Autocomplete, Checkbox, Stack } from '@mui/material';
import { Show, TextField } from '@side/pantry';
import { isEqual, upperFirst } from 'lodash';
import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Spinner } from '@/components/Spinner';
import { useCore, useIdentity, usePayment } from '$api-clients/hooks';
import { AgentFilter } from '../../components/AgentFilter';
import { CloseDateFilter } from '../../components/CloseDateFilter';
import { CustomDateRangePickers } from '../../components/CustomDateRangePickers';
import { HideTerminatedAgentsFilter } from '../../components/HideTerminatedAgentsFilter';
import { TeamFilter } from '../../components/TeamFilter';
import { ReportFiltersDrawer } from '../../ReportFiltersDrawer/ReportFiltersDrawer';
import {
  useReportsRole,
  useRoleCanSee,
} from '../../ReportsConfiguration/useReportsRole';
import { REPORTING_PERIOD_CUSTOM } from '../../types';
import {
  PERFORMANCE_REPORT_CONTEXT_DEFAULTS,
  usePerformanceReportContext,
} from '../context/PerformanceReportContext';
import {
  type PerformanceReportFiltersFormValues,
  PerformanceReportFilterTransactionStatus,
  PerformanceReportFilterTransactionType,
} from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  viewingAs: string;
}

const defaultValues = PERFORMANCE_REPORT_CONTEXT_DEFAULTS.filters;

export function PerformanceReportFilters({ open, onClose, viewingAs }: Props) {
  const { data: userinfo, isLoading: isLoadingUser } = useIdentity(
    '/users/{userId}',
    { params: { path: { userId: viewingAs } } },
  );
  const { data: config, isLoading: isLoadingConfig } = usePayment(
    '/reporting/config',
    {
      params: {
        query: {
          viewReportAs: viewingAs,
        },
      },
    },
  );

  const isLoading = isLoadingUser || isLoadingConfig;

  const { isAgent } = useReportsRole();
  const { roleCanSee } = useRoleCanSee();
  const { filters, updateContext } = usePerformanceReportContext();

  const { control, setValue, handleSubmit, reset, formState } =
    useForm<PerformanceReportFiltersFormValues>({
      defaultValues: filters,
      values: filters,
    });
  const { data: leadSourcesData = [], isLoading: isLoadingLeadSources } =
    useCore('/lead-sources');

  const leadSourceOptions = useMemo(
    () =>
      leadSourcesData.flatMap((source) =>
        source !== null ? { label: source.name, value: source.id } : [],
      ),
    [leadSourcesData],
  );

  const currentValues = useWatch({ control });
  const isClearDisabled = isEqual(currentValues, defaultValues);
  const isCustomRange =
    currentValues.reportingPeriod === REPORTING_PERIOD_CUSTOM;

  const handleFilterToOnlyMe = () => {
    if (!userinfo) return;

    setValue('agents', [{ label: userinfo.displayName, value: userinfo.id }], {
      shouldDirty: true,
    });
  };

  const onSubmitCallback = (data: PerformanceReportFiltersFormValues) => {
    updateContext({ filters: data });
    onClose();
  };

  const handleClear = () => {
    reset(defaultValues, { keepDefaultValues: true });
  };

  return (
    <ReportFiltersDrawer
      open={open}
      onClose={onClose}
      title="Performance Report"
      isSubmitDisabled={!formState.isDirty}
      onClear={handleClear}
      onSubmit={handleSubmit(onSubmitCallback)}
      isClearDisabled={isClearDisabled}
    >
      {isLoading || isLoadingLeadSources ? (
        <Spinner centered />
      ) : (
        <Stack direction="column" sx={{ gap: 3 }}>
          <CloseDateFilter
            name="reportingPeriod"
            control={control}
            startDate={config?.closingDateStart}
          />
          <Show when={isCustomRange}>
            <CustomDateRangePickers
              control={control}
              setValue={setValue}
              eventContainerPrefix="performance-report"
              required={isCustomRange}
              minDate={config?.closingDateStart}
            />
          </Show>
          <Show when={!isAgent}>
            <AgentFilter
              agentIdentityIds={config?.agentIdentityIds}
              name="agents"
              control={control}
              onFilterToOnlyMe={handleFilterToOnlyMe}
            />
          </Show>
          <Show when={roleCanSee('team')}>
            <TeamFilter
              name="teams"
              control={control}
              teamIdentityIds={config?.teamIdentityIds}
            />
          </Show>
          <Controller
            name="leadSources"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={leadSourceOptions}
                value={field.value}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(_event, newValue) => field.onChange(newValue)}
                renderOption={(props, option, { selected }) => (
                  <Stack direction="row" component="li" {...props}>
                    <Checkbox
                      disableRipple
                      checked={selected}
                      sx={{ mr: 2, p: 0 }}
                    />
                    {option.label}
                  </Stack>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lead Source"
                    placeholder="Select to filter"
                    fullWidth
                  />
                )}
              />
            )}
          />
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={[
                  PerformanceReportFilterTransactionStatus.pending,
                  PerformanceReportFilterTransactionStatus.closed,
                ]}
                value={field.value}
                getOptionLabel={(option) => upperFirst(option)}
                onChange={(_event, newValue) => field.onChange(newValue)}
                renderOption={(props, option, { selected }) => (
                  <Stack direction="row" component="li" {...props}>
                    <Checkbox
                      disableRipple
                      checked={selected}
                      sx={{ mr: 2, p: 0 }}
                    />
                    {upperFirst(option)}
                  </Stack>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status"
                    placeholder="Select to filter"
                    fullWidth
                  />
                )}
              />
            )}
          />
          <Controller
            name="transactionType"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={[
                  PerformanceReportFilterTransactionType.listing,
                  PerformanceReportFilterTransactionType.offer,
                ]}
                value={field.value}
                getOptionLabel={(option) =>
                  upperFirst(option.toLocaleLowerCase())
                }
                onChange={(_event, newValue) => field.onChange(newValue)}
                renderOption={(props, option, { selected }) => (
                  <Stack direction="row" component="li" {...props}>
                    <Checkbox
                      disableRipple
                      checked={selected}
                      sx={{ mr: 2, p: 0 }}
                    />
                    {upperFirst(option.toLocaleLowerCase())}
                  </Stack>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Transaction Type"
                    placeholder="Select to filter"
                    fullWidth
                  />
                )}
              />
            )}
          />
          <HideTerminatedAgentsFilter
            name="hideTerminatedAgents"
            control={control}
          />
        </Stack>
      )}
    </ReportFiltersDrawer>
  );
}
